import { createContext, useContext, useEffect, useState, useRef } from "react";
import { LoopOidc, LoopOidcConfig } from './api/loopoidc';
import { icons } from "daymap-library";



const AuthContext = createContext({});

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({children}) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const [settings, setSettings] = useState(null);
    const [signOutMenu, setSignOutMenu] = useState(null);
    const [clientSettings, setClientSettings] = useState({
        zoomAmount: 100,
        openDefaultOnStart: false,
        defaultAppType: 'Check In',
        defaultCheckInMode: null,
        defaultKioskMode: null,
        defaultScheduledEventMode: null 
    });
    const { idserver } = useDiscovery();
    const mngr = useRef(null);
    
    const startAuth = async () => {
        let checkinUrl = `${window.location.origin}/kiosk`
        if (process.env.REACT_APP_CHECKIN_URL)
            checkinUrl = process.env.REACT_APP_CHECKIN_URL;

        const config = new LoopOidcConfig('Checkin.API', idserver, `${checkinUrl}/auth`);
        config.acr_values = config.redirect_uri;
        config.silent_redirect_uri = `${checkinUrl}/authsilent`;
        mngr.current = new LoopOidc(config, { forcePrompt: true });

        let token;
        switch (window.location.pathname) {
            case '/kiosk/auth':
                const user = await mngr.current.OidcManager.signinCallback();
                if (user) {
                    if (user.state) {
                        window.location = user.state;
                        break;
                    }
                }
                
                token = await mngr.current.GetAccessToken();
                
                break;
            case '/kiosk/authsilent':
                await mngr.current.OidcManager.signinSilentCallback();
                break;
            default:
                token = await mngr.current.GetAccessToken();
        } 
        if (token) {
            await loadSettings();
            setIsAuthenticated(true);
        }
    };

    useEffect(() => {
        if (idserver)
            startAuth();       
    }, [idserver]);

    const lmsFetch = (url, data = null, config = {}) => {
        const controller = new AbortController();
        const signal = controller.signal;

        if (process.env.REACT_APP_LMS_API_ORIGIN)
            url = `${process.env.REACT_APP_LMS_API_ORIGIN}${url}`;

        const execute = async () => {
            let response = {};
            try {
                response = await mngr.current.GetResponse(url, data, { ...config, signal, headers: { "Content-Type":"application/json" } });
                switch(config.returns){
                    case 'blob':
                        response.data = await response.blob();
                        break;
                    case 'text':
                        response.data = await response.text();
                        break;
                    case 'raw':
                        return response;
                    default:
                        response.data = await response.json();
                        break;
                }
            } catch (error) {
                response.error = error;
            }
            return response;
        }

        const cancel = () => {
            console.log('cancelling fetch to ' + url);
            controller.abort();
        }

        return [execute, cancel];
    }

    const loadSettings = async () => {
        const [getSettings] = lmsFetch('/lms/api/checkin/settings');
        const response = await getSettings();

        if(response.error){
            if(response.error.message == "Unauthorized"){
                throw new Error("Your IP Address is not authorised to access this site.  Please contact your Daymap Administrator" );
            }
        }
        let storedSettings = { ...clientSettings }; 

        const savedSettings = window.localStorage.getItem('clientSettings');
        if (savedSettings)
            storedSettings = JSON.parse(savedSettings);
        else {
            storedSettings.defaultCheckInMode = response.data.CheckInModes && response.data.CheckInModes.length > 0 ? response.data.CheckInModes[0] : null;
            storedSettings.defaultKioskMode = response.data.KioskModes && response.data.KioskModes.length > 0 ? response.data.KioskModes[0] : null;
            storedSettings.defaultVisitorLocation = response.data.VisitorLocations && response.data.VisitorLocations.length > 0 ? response.data.VisitorLocations[0] : null;
        }

        setClientSettings(storedSettings);
        setSettings(response.data);
        if (response.data.SignOutMenuEnabled)
            setSignOutMenu({ id: 'Sign Out', text: 'Sign Out', iconCode: icons.ICON_CSS_CLASS.SignOut, onClick: signOut})
    }

    const saveClientSettings = () => {        
        window.localStorage.setItem('clientSettings', JSON.stringify(clientSettings));
    }

    const signOut = async () => {
        setIsSigningOut(true);
        try {
            await mngr.current.OidcManager.signoutSilent({ post_logout_redirect_uri: `${window.location.origin}/kiosk/authsignout` });
        } catch (error) {
            console.error(error);
        }
        window.location = `${window.location.origin}/kiosk`;
    }

    const value = { isAuthenticated, mngr: mngr.current, lmsFetch, settings, clientSettings, setClientSettings, saveClientSettings, isSigningOut, signOutMenu, signOut };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

const useDiscovery = () => {
    const [data, setData] = useState({});

    const getData = async () => {
        let origin = window.location.origin;
        if (process.env.REACT_APP_LMS_API_ORIGIN)
            origin = process.env.REACT_APP_LMS_API_ORIGIN;

        const url = `${origin}/daymap/dws/discover.ashx`;
        const response = await fetch(url);
        const discoverData = await response.json();

        setData(discoverData);
    }

    useEffect(() => {
        getData();
    }, []);

    return data;
}

export { useDiscovery };